import React, {useEffect, useState} from 'react';
import * as moment from 'moment-timezone';
import {useLazyQuery, useMutation} from "@apollo/client";
import {
    LinearProgress,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';

import {GET_DETAILED_SHIFT_PLANS, GENERATE_SHIFTS, DELETE_SHIFT_PLANS} from "../../queries/GraphQLQueries";
import ShiftPlanManagementDialogHeader from './ShiftPlanManagementDialogHeader';
import {SHIFT_SELECTION_STATUS} from "../../domain/ShiftPlanConstants";
import {SINGLE_DAY_DATE_FORMAT} from "../../config/DateTimeConstants";
import AddShiftPlanDialog from './AddShiftPlanDialog';
import CustomAlert from "../CustomAlert";
import {buildShiftPlanMap, SHIFT_PLAN_SUCCESS_MESSAGE} from "../../helpers/ShiftPlanHelper";
import ShiftPlanGrid from './ShiftPlanGrid';
import {BOOKABLE_STATUS} from "../../domain/BookableStatus";

export default function ShiftPlanManagementDialog({openShiftPlanManagementDialog, handleClose, selectedDate, shouldRefreshShiftPlans}) {

    const VIEW = 'day';
    const dialogStyle = {minWidth: "500px", height: "75%"};

    const [currentDate, setCurrentDate] = useState('');
    const [shiftPlans, setShiftPlans] = useState([]);
    const [addShiftPlanDialogOpen, setAddShiftPlanDialogOpen] = useState(false);
    const [shiftPlanAdded, setShiftPlanAdded] = useState(false);
    const [alertMessage, setAlertMessage] = useState(null);
    const [shiftGenerationEnabled, setShiftGenerationEnabled] = useState(false);
    const [deleteShiftPlanButtonEnabled, setDeleteShiftPlanButtonEnabled] = useState(false);
    const [selectedShiftPlanIds, setSelectedShiftPlanIds] = useState({type: 'include', ids: new Set()});
    const [shiftBookableStatuses, setShiftBookableStatuses] = useState(null);
    const [isShiftBeingGenerated, setIsShiftBeingGenerated] = useState(false);
    const [isShiftPlanDataLoading, setIsShiftPlanDataLoading] = useState(false);
    const refetchDetailedShiftPlans = {refetchQueries: [{query: GET_DETAILED_SHIFT_PLANS}, 'GetShiftPlansForTimeRange']};

    const [fetchShiftPlans, {loading, error, data}] = useLazyQuery(GET_DETAILED_SHIFT_PLANS, {fetchPolicy: 'network-only'});
    const [generateShifts] = useMutation(GENERATE_SHIFTS, refetchDetailedShiftPlans);
    const [deleteShiftPlans] = useMutation(DELETE_SHIFT_PLANS, refetchDetailedShiftPlans);

    useEffect(() => {
        setCurrentDate(selectedDate?.start);
    }, [selectedDate?.start]);

    useEffect(() => {
        updateGenerateShiftsButtonStatus(selectedShiftPlanIds);
        updateDeleteShiftPlanButtonStatus(selectedShiftPlanIds);
    }, [shiftPlans, currentDate, shiftPlanAdded, selectedShiftPlanIds]);

    useEffect(() => {
        if (openShiftPlanManagementDialog) {
            setIsShiftPlanDataLoading(true);
            fetchShiftPlans({
                variables: {
                    startTime: moment(currentDate).startOf(VIEW).toISOString(),
                    endTime: moment(currentDate).endOf(VIEW).toISOString()
                }
            });
            setShiftPlanAdded(false);
            setSelectedShiftPlanIds({ type: 'include', ids: new Set() });
        }
    }, [currentDate, shiftPlanAdded]);

    useEffect(() => {
        if (!data || !data.shiftPlans) {
            return;
        }

        let mappedShiftPlans = buildShiftPlanMap(data.shiftPlans);
        setShiftPlans(mappedShiftPlans);
        setIsShiftPlanDataLoading(false);
    }, [data]);

    const handleCloseCustomAlert = () => setAlertMessage(null);
    const openAddShiftPlanDialog = () => setAddShiftPlanDialogOpen(true);

    const updateGenerateShiftsButtonStatus = (selectedShiftPlanIds) => {
        const isAnyShiftPlanSelected = selectedShiftPlanIds?.ids?.size > 0;
        const selectedIds = Array.from(selectedShiftPlanIds?.ids || []);

        const isAnyShiftPlanSelectedWithEnrolledPerformer = selectedIds.every(
          rowId => shiftPlans.find(shiftPlan => shiftPlan.id === rowId)?.enrollments.some(
            enrollment => enrollment.shiftSelectionStatus === SHIFT_SELECTION_STATUS.SELECTED
              && enrollment.availability === null
          ));

        setShiftGenerationEnabled(isAnyShiftPlanSelected && isAnyShiftPlanSelectedWithEnrolledPerformer);
    };

    const handleGenerateShifts = () => {
        if (isShiftBeingGenerated) {
            return;
        }

        setIsShiftBeingGenerated(true);

        const latestGenerateShiftData = Array.from(selectedShiftPlanIds?.ids).map(shiftPlanId => {
            const shiftPlan = shiftPlans.find(plan => plan.id === shiftPlanId);
            if (!shiftPlan){
                return null;
            }

            if (!shiftBookableStatuses || !shiftBookableStatuses[shiftPlanId]) {

                if (shiftPlan.bookableServiceTypePlans && shiftPlan.bookableServiceTypePlans.length > 0) {
                    const defaultBookableServices = shiftPlan.bookableServiceTypePlans.map(service => ({
                        id: service.id,
                        isBookable: true
                    }));

                    return {
                        shiftPlanId,
                        bookableServiceTypes: defaultBookableServices
                    };
                }
                return null;
            }

            const bookableServiceTypes = shiftBookableStatuses[shiftPlanId];
            const serviceTypeGroups = {};
            shiftPlan.bookableServiceTypePlans.forEach((service, index) => {
                const type = service.serviceType.id;
                if (!serviceTypeGroups[type]) {
                    serviceTypeGroups[type] = [];
                }
                serviceTypeGroups[type].push({
                    id: service.id,
                    isBookable: bookableServiceTypes[index] === BOOKABLE_STATUS.Bookable
                });
            });

            const mappedBookableServiceTypes = Object.values(serviceTypeGroups).flat();

            return {
                shiftPlanId,
                bookableServiceTypes: mappedBookableServiceTypes
            };
        }).filter(item => item !== null);

        generateShifts({
            variables: {shiftPlans: latestGenerateShiftData}
        })
        .then(() => setAlertMessage({children: SHIFT_PLAN_SUCCESS_MESSAGE.SHIFTS_GENERATED, severity: 'success'}))
        .catch(error => {
            setAlertMessage({children: error?.message, severity: 'error'});
            console.log("error " + error)
        })
        .finally(() => {
            setIsShiftBeingGenerated(false);
            setSelectedShiftPlanIds({ type: 'include', ids: new Set() });
        });
    };

    const handleShiftPlanAdded = () => {
      setShiftPlanAdded(true);
      setAddShiftPlanDialogOpen(false);
      shouldRefreshShiftPlans(true);
    };

    const doShiftPlansHaveGeneratedShifts = (selectedShiftPlanIds) => {
        return Array.from(selectedShiftPlanIds?.ids).every(rowId => {
            const shiftPlan = shiftPlans.find(shiftPlan => shiftPlan.id === rowId);
            if (!shiftPlan) {
                return false;
            }

            return shiftPlan.enrollments.every(enrollment => enrollment.availability === null);
        });
    };

    const updateDeleteShiftPlanButtonStatus = (selectedShiftPlanIds) => {
        const isAnyShiftPlanSelected = selectedShiftPlanIds?.ids.size > 0;
        const doAnyShiftPlansHaveGeneratedShifts = doShiftPlansHaveGeneratedShifts(selectedShiftPlanIds);
        const canEnableDeleteShiftPlan = isAnyShiftPlanSelected && doAnyShiftPlansHaveGeneratedShifts;

        setDeleteShiftPlanButtonEnabled(canEnableDeleteShiftPlan);
    };

    const handleDeleteShiftPlans = () => {
        setIsShiftPlanDataLoading(true);
        const selectedIds = Array.from(selectedShiftPlanIds?.ids);
        deleteShiftPlans({variables: {shiftPlanIds: selectedIds}})
        .then(() => setAlertMessage({children: SHIFT_PLAN_SUCCESS_MESSAGE.SHIFT_PLANS_DELETED, severity: 'success'}))
        .catch(error => {
            setAlertMessage({children: error?.message, severity: 'error'});
            console.log("error " + error)
        })
        .finally(() => setSelectedShiftPlanIds({ type: 'include', ids: new Set() }));
    };

    const moveToNextDay = () => {
        const nextDay = new Date(currentDate);
        nextDay.setDate(nextDay.getDate() + 1);
        setCurrentDate(nextDay);
    };

    const moveToPreviousDay = () => {
        const previousDay = new Date(currentDate);
        previousDay.setDate(previousDay.getDate() - 1);
        setCurrentDate(previousDay);
    };

    return <>
        <Dialog open={openShiftPlanManagementDialog} maxWidth={'lg'} fullWidth onClose={handleClose} PaperProps={{sx: dialogStyle}}>
            <DialogTitle>
                <ShiftPlanManagementDialogHeader currentDate={moment(currentDate).format(SINGLE_DAY_DATE_FORMAT)} handlePreviousDay={moveToPreviousDay} handleNextDay={moveToNextDay} handleClose={handleClose} />
            </DialogTitle>
            {loading && <LinearProgress/>}
            {!loading && <Box sx={{my: 0.25}}></Box>}
            <DialogContent>
                {
                  !isShiftPlanDataLoading
                  &&
                  <ShiftPlanGrid
                    isDataLoading={isShiftPlanDataLoading}
                    shiftPlans={shiftPlans}
                    selectedShiftPlanIds={selectedShiftPlanIds}
                    setSelectedShiftPlanIds={setSelectedShiftPlanIds}
                    setShiftBookableStatuses={setShiftBookableStatuses}/>
                }
            </DialogContent>
            <DialogActions>
                <Button sx={{marginLeft: '1rem', marginBottom: '0.5rem'}} variant="contained" onClick={openAddShiftPlanDialog}>Add</Button>
                <Button sx={{marginRight: 'auto', marginBottom: '0.5rem'}} variant="contained" onClick={handleDeleteShiftPlans} disabled={!deleteShiftPlanButtonEnabled}>Delete</Button>
                <Button sx={{marginBottom: '0.5rem', marginRight: '0.9rem'}} variant="contained" onClick={handleGenerateShifts} disabled={!shiftGenerationEnabled || isShiftBeingGenerated}>Generate Shifts</Button>
            </DialogActions>

            <AddShiftPlanDialog
              openAddShiftPlanDialog={addShiftPlanDialogOpen}
              handleClose={() => setAddShiftPlanDialogOpen(false)}
              onShiftPlanAdded={handleShiftPlanAdded}
              currentDate={currentDate} />
        </Dialog>
        <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert} />
    </>;
};